import React, { useMemo } from 'react';

import { Col } from '@opendoor/atoms/box';
import type { HubBuyingOffer } from 'components/hub/useHubPurchaseOffers';

import { HubSectionHeader } from './HubSectionHeader';
import { TransactionCardSkeleton } from './HubSkeletons';
import { buildMortgageLoanCardModel } from './mortgageLoanCardModel';
import { buildOfferCardModel } from './offerCardModel';
import { TransactionCard } from './TransactionCard';
import { useMortgageLoan } from './useMortgageLoan';

interface MortgageSectionProps {
  /**
   * The buyer's purchase offers, used to borrow the home's photo for the card —
   * an in-progress mortgage is for the home being purchased, so this mirrors the
   * image shown on the Home purchase card.
   */
  offers: HubBuyingOffer[];
}

// The active purchase offer's photo, matching the Home purchase card's image.
const usePurchasePhotoUrl = (offers: HubBuyingOffer[]): string | null =>
  useMemo(() => {
    const models = offers.map(buildOfferCardModel);
    const active = models.find((offer) => !offer.isCanceled) ?? models[0];
    return active?.photoUrl ?? null;
  }, [offers]);

/**
 * Buyer-hub mortgage section. Surfaces the buyer's active Opendoor Home Loans
 * application alongside their home purchase/sale. Like the other task sections,
 * a buyer with no active loan does not see it once data loads; while loading we
 * keep the section and show a skeleton so the page doesn't shift.
 */
export const MortgageSection: React.FC<MortgageSectionProps> = ({ offers }) => {
  const { loan, isLoading } = useMortgageLoan();
  const propertyPhotoUrl = usePurchasePhotoUrl(offers);

  if (!isLoading && !loan) {
    return null;
  }

  return (
    <Col className="w-full gap-4">
      <HubSectionHeader title="Mortgage" />
      {isLoading || !loan ? (
        <TransactionCardSkeleton />
      ) : (
        <TransactionCard
          section="mortgage"
          transaction={buildMortgageLoanCardModel(loan, propertyPhotoUrl)}
        />
      )}
    </Col>
  );
};
